import { useRef, useCallback } from 'react';

export interface AbortableRequestControls {
  /** Get a new AbortSignal, aborting any previous request */
  getSignal: () => AbortSignal;
  /** Check if this controller is still the current one */
  isCurrent: (controller: AbortController) => boolean;
  /** Abort the current request */
  abort: () => void;
}

/**
 * Hook for managing abortable async requests.
 * Ensures only the latest request completes, cancelling any pending ones.
 */
export function useAbortableRequest(): AbortableRequestControls {
  const controllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  const getSignal = useCallback(() => {
    // Abort any pending request
    abort();
    // Create new controller
    const controller = new AbortController();
    controllerRef.current = controller;
    return controller.signal;
  }, [abort]);

  const isCurrent = useCallback((controller: AbortController) => {
    return controllerRef.current === controller;
  }, []);

  return { getSignal, isCurrent, abort };
}

/**
 * Check if an error is an AbortError (request was cancelled)
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}
